import React, { useEffect, useRef, useState } from "react";
import { OldHouseSVG } from "@/components/HouseSVG";
import { Sparkles, ArrowRight } from "lucide-react";
import { t } from "@/i18n";

const AFTER_PHOTO =
  "https://customer-assets.emergentagent.com/wingman/b351a7fb-087a-4fca-96f5-8e5804986d90/attachments/0854497832994987a9bb4a3d5886a8e8_1000.jpg";

const BeforeAfter = ({ lang }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // 0..100 (% from left)
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      setFromClientX(e.clientX);
    };
    const up = () => {
      dragging.current = false;
    };
    window.addEventListener("pointerup", up);
    window.addEventListener("pointermove", move);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointermove", move);
    };
  }, []);

  return (
    <section className="mt-12" data-testid="showcase-section">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold-dark text-xs font-bold tracking-wide">
        <Sparkles className="h-3.5 w-3.5" />
        {t(lang, "showcase_eyebrow")}
      </div>
      <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-balance">
        {t(lang, "showcase_title")}{" "}
        <span className="gold-shimmer animate-shimmer">
          {t(lang, "showcase_title_hl")}
        </span>
      </h2>
      <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">
        {t(lang, "showcase_sub")}
      </p>

      {/* Comparison slider */}
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        className="relative mt-5 w-full aspect-[16/10] rounded-2xl overflow-hidden border-2 border-navy bg-navy-50 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.45)] touch-none select-none cursor-ew-resize"
        data-testid="before-after-slider"
        role="slider"
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* AFTER (full underlay) — real project photo */}
        <div className="absolute inset-0">
          <img
            src={AFTER_PHOTO}
            alt="Modern Mera Naksha completed home"
            loading="lazy"
            draggable={false}
            className="h-full w-full object-cover"
            data-testid="after-photo"
          />
        </div>
        {/* BEFORE (clipped overlay) */}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${pos}%` }}
        >
          <div className="h-full" style={{ width: `${(100 / Math.max(pos, 0.0001)) * 100}%` }}>
            <OldHouseSVG className="h-full w-full" />
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-navy/90 text-white text-[11px] font-bold uppercase tracking-wider">
          {t(lang, "before_label")}
        </div>
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-gold text-navy text-[11px] font-bold uppercase tracking-wider">
          {t(lang, "after_label")}
        </div>

        {/* Divider */}
        <div
          className="absolute inset-y-0"
          style={{ left: `calc(${pos}% - 1px)` }}
        >
          <div className="h-full w-0.5 bg-gold shadow-[0_0_0_1px_rgba(11,37,69,0.6)]" />
          <button
            type="button"
            onPointerDown={onPointerDown}
            data-testid="before-after-handle"
            aria-label="Drag to compare"
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 h-11 w-11 rounded-full bg-gold border-2 border-navy text-navy grid place-items-center shadow-lg cursor-ew-resize"
          >
            <ArrowRight className="h-3.5 w-3.5 -mr-1" />
            <ArrowRight className="h-3.5 w-3.5 rotate-180 -ml-1" />
          </button>
        </div>
      </div>

      {/* Captions */}
      <div className="mt-3 grid grid-cols-2 gap-3 text-[12.5px]">
        <div className="rounded-xl bg-white border border-border p-3" data-testid="before-caption">
          <div className="font-bold text-navy">{t(lang, "before_label")}</div>
          <div className="text-muted-foreground">{t(lang, "before_caption")}</div>
        </div>
        <div className="rounded-xl bg-navy text-white p-3" data-testid="after-caption">
          <div className="font-bold text-gold">{t(lang, "after_label")}</div>
          <div className="text-white/80">{t(lang, "after_caption")}</div>
        </div>
      </div>

      {/* Chips */}
      <div className="mt-3 flex flex-wrap gap-2">
        {["showcase_chip_1", "showcase_chip_2", "showcase_chip_3"].map((k) => (
          <span
            key={k}
            className="px-3 py-1.5 rounded-full bg-navy-50 text-navy border border-navy/15 text-[12px] font-semibold"
          >
            ✓ {t(lang, k)}
          </span>
        ))}
      </div>
    </section>
  );
};

export default BeforeAfter;
